// The board's furniture, shared by the team's board and the client's.
//
// **There is one board.** The client's seat renders the same sheet the team
// works in — same grid, same subgrid rows, same group bands, same sticky header —
// with two columns taken out and two left read-only. Before this file there were
// two implementations of the same table, which is the arrangement where the
// client's page quietly drifts until it is showing something the team is not.
//
// What lives here is the FURNITURE, never the cells. A team row renders selects
// and date inputs and dispatches events; a client row renders text. Trying to
// share the cells as well would mean a component that takes a dozen callbacks and
// is read by nobody, so the split is: this file owns the grid, each side owns what
// goes in it.
//
// The column tracks themselves stay in `global.css` (`--board-cols`, and the
// `--client` variant beside it), because the container queries that drop columns
// on a narrow board have to know which ordinal is which — and a track list split
// across a stylesheet and a prop is the drift this file exists to prevent.

import type { CSSProperties, ReactNode } from 'react'

import { Icon } from './Icon'
import { LensChip, type Lens } from './Page'
import { TextMenu } from './Picker'

/** Which column, and which way. `board` is the authored order — the one you get
 *  back by clicking the sorted column until it clears, so a sort is always a lens
 *  over the order someone chose rather than a change to it. */
export interface Sort {
  key: string
  dir: 'asc' | 'desc'
}

export interface BoardColumn {
  key: string
  label: string
  sortable: boolean
  /** pushed to the right-hand edge of its track — the actions/notes column */
  end?: boolean
}

/**
 * Third click returns the authored order rather than cycling forever, so there is
 * always a way back to what someone arranged by hand.
 */
export function nextSort(sort: Sort, key: string): Sort {
  if (sort.key !== key) return { key, dir: 'asc' }
  return sort.dir === 'asc' ? { key, dir: 'desc' } : { key: 'board', dir: 'asc' }
}

/**
 * The sheet: a bordered surface that is also the containment context, so the board
 * sizes against the COLUMN it is in rather than the window. It lives at three
 * widths — full-bleed at /b/:workId, inside a ~740px main column on the project
 * page, and inside the client's document — and a viewport media query cannot tell
 * those apart.
 */
export function BoardSheet({ variant, children }: { variant?: 'client'; children: ReactNode }) {
  return (
    <div className={variant === 'client' ? 'board-sheet board-sheet--client' : 'board-sheet'}>
      {children}
    </div>
  )
}

/**
 * The one set of headers.
 *
 * A SIBLING of `.board`, not a row inside it: a sticky grid item can only move
 * within its own grid area, and a full-width header row occupies exactly one — so
 * it had nowhere to travel and simply never stuck, whatever `top` said.
 *
 * `onSort` is optional. Without it the labels render as labels, which is what a
 * board nobody can reorder should look like.
 */
export function BoardHeaders({
  columns,
  sort,
  onSort,
}: {
  columns: BoardColumn[]
  sort?: Sort
  onSort?: (s: Sort) => void
}) {
  return (
    <div className="board-head" role="row">
      {columns.map((c) =>
        c.sortable && sort && onSort ? (
          <button
            key={c.key}
            type="button"
            onClick={() => onSort(nextSort(sort, c.key))}
            aria-sort={
              sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
            }
            style={c.end ? { justifyContent: 'flex-end' } : undefined}
          >
            {c.label}
            {sort.key === c.key && (
              <Icon name={sort.dir === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={12} />
            )}
          </button>
        ) : (
          <span key={c.key} style={c.end ? { justifyContent: 'flex-end' } : undefined}>
            {c.label}
          </span>
        ),
      )}
    </div>
  )
}

/** The grid itself. Every row inside it is a `subgrid`, which is what makes a date
 *  in one group sit under a date in the next. */
export function BoardGrid({ children }: { children: ReactNode }) {
  return <div className="board">{children}</div>
}

/**
 * A band across the sheet, not a floating heading: it separates two runs of rows,
 * so it is ruled above and below and tinted like the header it echoes.
 *
 * `name` and `right` are slots because the team edits both (an EditableText and a
 * date input) and the client reads both.
 */
export function BoardGroupBand({
  name,
  count,
  right,
}: {
  name: ReactNode
  count: ReactNode
  right?: ReactNode
}) {
  return (
    <div className="board-group">
      {name}
      <span className="meta">{count}</span>
      {right && (
        <span className="flex w-full items-center gap-3 sm:ml-auto sm:w-auto sm:gap-2.5">
          {right}
        </span>
      )}
    </div>
  )
}

/** One row, as a subgrid across the sheet's tracks. */
export function BoardRow({
  children,
  className = '',
  ...rest
}: { children: ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  // `board-row` carries the subgrid, so a caller's class is added to it rather than
  // replacing it — spreading `rest` over a fixed className would have dropped the
  // columns on the floor the first time anyone passed one.
  return (
    <div className={`board-row ${className}`.trim()} role="row" {...rest}>
      {children}
    </div>
  )
}

/**
 * A blocked row shows a label, not a control: its status is DERIVED from the
 * edges, so offering to set it would be offering something that cannot be done.
 * The client sees the identical chip, because a row that is stuck should read as
 * stuck on both sides of the wire.
 */
export function BlockedChip({ blockers }: { blockers: number }) {
  return (
    <span
      className="pill justify-self-start"
      title={`Derived from ${blockers === 1 ? 'its dependency' : 'its dependencies'}`}
      style={{ '--pc': 'var(--crit)' } as CSSProperties}
    >
      <span className="tick" />
      Blocked
    </span>
  )
}

/**
 * What to look at. Defaults to everything: a board that hides what it delivered is
 * a board that cannot show a client what it delivered, and finished rows are the
 * record of the work. Narrowing is a choice you make, not one the table makes for
 * you the moment you tick something.
 */
export type Only = 'all' | 'open' | 'done'

/**
 * How the rows are ordered, said out loud.
 *
 * The board always had an order — creation order, from the ULID in each row's id —
 * and no way to see that, change it, or even know it was the rule. The column headers
 * could sort, which nobody found, and nothing could be arranged by hand at all.
 *
 * `board` is the manual one, and it is the default: the order somebody dragged the
 * rows into. Everything else is a LENS over that, which is why dragging turns off
 * while one is on — a drag that cannot survive the sort it was dropped into is a
 * control that lies.
 */
export const ORDERINGS = [
  { key: 'board', label: 'Board order' },
  { key: 'item', label: 'Name' },
  { key: 'status', label: 'Status' },
  { key: 'due', label: 'Due' },
  { key: 'owner', label: 'Owner' },
  { key: 'theirs', label: 'Priority' },
] as const

export function OrderBy({ value, onChange }: { value: Sort; onChange: (s: Sort) => void }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="meta">Order by</span>
      <TextMenu
        label="Order by"
        chevron
        value={value.key}
        onPick={(key) => onChange({ key, dir: 'asc' })}
        options={ORDERINGS.map((o) => ({ key: o.key, label: o.label }))}
      />
      {value.key !== 'board' && (
        <button
          type="button"
          onClick={() => onChange({ key: value.key, dir: value.dir === 'asc' ? 'desc' : 'asc' })}
          className="meta"
          title={value.dir === 'asc' ? 'Ascending' : 'Descending'}
        >
          {value.dir === 'asc' ? '▲' : '▼'}
        </button>
      )}
    </span>
  )
}

export function OnlyFilter({ value, onChange }: { value: Only; onChange: (v: Only) => void }) {
  return (
    <span className="flex items-center gap-1.5">
      {ONLY_LENSES.map((lens) => (
        <LensChip
          key={lens.key}
          lens={lens}
          active={lens.key === value}
          onPick={(k) => onChange(k as Only)}
        />
      ))}
    </span>
  )
}

/** The board's three, as lenses. Same chip as every other lens in the app: this
 *  narrows what the sheet shows, which is exactly what a lens does. */
const ONLY_LENSES: Lens[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'done', label: 'Done' },
]

/** `only`, applied. Shared so the two boards cannot disagree on what "open" means. */
export function applyOnly<T extends { status?: string; done?: boolean }>(
  list: T[],
  only: Only,
): T[] {
  if (only === 'all') return list
  return list.filter((i) => {
    const done = i.done ?? i.status === 'done'
    return (only === 'done') === done
  })
}
